import Box from "@mui/material/Box";
import {
  ReactNode,
  TransitionEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export type RollingCubeMotion = {
  durationMs: number;
  delayMs?: number;
  reduceMotion?: boolean;
};

const ROLL_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const PERSPECTIVE_PX = 700;

const RollingCube = ({
  children,
  next,
  showNext = false,
  durationMs = 500,
  delayMs = 0,
  onClick,
}: {
  children: ReactNode;
  next: ReactNode;
  showNext?: boolean;
  durationMs?: number;
  delayMs?: number;
  onClick?: () => void;
}) => {
  const frontRef = useRef<HTMLDivElement>(null);
  const rollingRef = useRef(false);
  const finishRollRef = useRef(() => {});
  const [faceHeight, setFaceHeight] = useState(0);
  const [settledNext, setSettledNext] = useState(showNext);
  const [settledStep, setSettledStep] = useState(0);
  const [rolling, setRolling] = useState(false);
  const settledIndex = settledStep % 4;
  const rollStep = rolling ? settledStep + 1 : settledStep;
  const settledFace = settledNext ? next : children;
  const incomingFace = settledNext ? children : next;

  finishRollRef.current = () => {
    if (!rollingRef.current) {
      return;
    }

    rollingRef.current = false;
    setRolling(false);
    setSettledStep((step) => step + 1);
    setSettledNext((current) => !current);
  };

  useEffect(() => {
    if (showNext !== settledNext && !rolling) {
      rollingRef.current = true;
      setRolling(true);
    }
  }, [showNext, settledNext, rolling]);

  useEffect(() => {
    if (!rolling) {
      return;
    }

    const timeoutId = window.setTimeout(
      () => finishRollRef.current(),
      durationMs + delayMs + 80
    );

    return () => window.clearTimeout(timeoutId);
  }, [rolling, durationMs, delayMs]);

  useLayoutEffect(() => {
    const face = frontRef.current;

    if (!face) {
      return;
    }

    const measure = () => setFaceHeight(face.offsetHeight);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(face);

    return () => observer.disconnect();
  }, [settledIndex, faceHeight > 0]);

  const faceStyle = (transform: string) => ({
    position: "absolute" as const,
    top: 0,
    left: 0,
    width: "100%",
    backfaceVisibility: "hidden" as const,
    transform,
  });

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : undefined,
        perspective: `${PERSPECTIVE_PX}px`,
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: faceHeight || undefined,
          transformStyle: "preserve-3d",
          transformOrigin: "center center",
          transform: `rotateX(${rollStep * -90}deg)`,
          transition: faceHeight
            ? `transform ${durationMs}ms ${ROLL_EASE} ${delayMs}ms`
            : "none",
        }}
        onTransitionEnd={(event: TransitionEvent) => {
          if (
            event.target !== event.currentTarget ||
            event.propertyName !== "transform"
          ) {
            return;
          }

          finishRollRef.current();
        }}
      >
        {faceHeight > 0 ? (
          [settledIndex, (settledIndex + 1) % 4].map((index) => (
            <Box
              key={index}
              ref={index === settledIndex ? frontRef : undefined}
              aria-hidden={
                index !== (rolling ? (settledIndex + 1) % 4 : settledIndex)
              }
              sx={faceStyle(
                `rotateX(${index * 90}deg) translateZ(${
                  faceHeight / 2
                }px) scale(${
                  (PERSPECTIVE_PX - faceHeight / 2) / PERSPECTIVE_PX
                })`
              )}
            >
              {index === settledIndex ? settledFace : incomingFace}
            </Box>
          ))
        ) : (
          <Box ref={frontRef}>{settledFace}</Box>
        )}
      </Box>
    </Box>
  );
};

export default RollingCube;
